import {AnimatePresence, type Transition, motion} from "framer-motion"
import {FC, useEffect, useMemo, useState} from "react"

import {UiCauses} from "@ri2/app/server/routers/getUiCauses"
import {CauseOnMessageWithCauseAndPart} from "@ri2/db/client"
import {css} from "@styled-system/css"

import {Cause} from "./cause"
import {generateIconBackgrounds} from "./utils"

interface CausesListProps {
  causes: CauseOnMessageWithCauseAndPart[]
  caseId: string
  bucket: keyof UiCauses
}

const SPRING: Transition = {
  type: "spring",
  damping: 25,
  stiffness: 120,
  duration: 2,
}

export const CausesList: FC<CausesListProps> = ({causes, caseId, bucket}) => {
  const [highlightedCauseId, setHighlightedCause] = useState<number | null>(
    null,
  )

  const iconBackgrounds = useMemo(
    () => generateIconBackgrounds(causes.length),
    [causes.length],
  )

  useEffect(() => {
    if (bucket === "confirmed") {
      setHighlightedCause(causes[0]?.id)
    }
  }, [bucket, causes])

  return (
    <ul
      className={css(
        {
          display: "flex",
          flexDirection: "column",
        },
        bucket === "confirmed" && {
          gap: 8,
        },
      )}
    >
      {/* The key is used to stop it from animating when the cause likelihood changes, since that's just the user toggling between different lists */}
      <AnimatePresence initial={false}>
        <h2
          className={css({
            color: "text.primary",
            textStyle: "bodyLargeMedium",
            letterSpacing: "-0.01em",
            marginTop: 15,
            marginBottom: 5,
          })}
        >
          {getBannerHeader(bucket as keyof UiCauses)}
        </h2>

        {causes.map((causeOnMessage, index) => (
          <motion.li
            key={causeOnMessage.causeId}
            layout
            transition={SPRING}
            initial={{
              opacity: 0,
              y: 0,
              scale: 1,
              zIndex: causes.length - index,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              zIndex: causes.length - index,
            }}
            exit={{
              opacity: 0,
              y: 50,
              scale: 0.9,
              zIndex: causes.length - index,
            }}
          >
            <Cause
              caseId={caseId}
              iconBackground={iconBackgrounds[index]}
              cause={causeOnMessage}
              bucket={bucket}
              setHoveredItem={setHighlightedCause}
              isHighlighted={highlightedCauseId === causeOnMessage.id}
            />
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  )
}

const getBannerHeader = (bucket: keyof UiCauses): string => {
  if (bucket === "confirmed") return "Confirmed"
  if (bucket === "veryLikely") return "Very Likely"
  if (bucket === "moreLikely") return "More Likely"
  return "Possible"
}
